import { useEffect } from 'react';
import { useEditor } from 'tldraw';
import { useBoardAiContext } from '../context/BoardAiContext';
import { useBoardStore } from '../store/useBoardStore';
import { digestDeleteListenersSuppressed } from '../utils/boardConstraintsDigest';
import { isCanvasEvidenceShape } from '../utils/pasteCanvasEvidence';

/**
 * Digest-delete pasted/typed evidence when the shape is removed.
 * Typed-text commit/delete is also handled in TextShapeDigest; duplicate
 * deletes coalesce on the server.
 */
export default function CanvasEvidenceDigest() {
  const editor = useEditor();
  const { enqueueDigestDelete } = useBoardAiContext();
  const canEdit = useBoardStore(s => Boolean(s.canEdit || s.isOwner));

  useEffect(() => {
    if (!editor || !canEdit || !enqueueDigestDelete) return undefined;

    const unlisten = editor.store.listen(
      (entry) => {
        if (digestDeleteListenersSuppressed.active) return;
        const removed = entry?.changes?.removed || {};
        const shapeIds = [];
        for (const record of Object.values(removed)) {
          if (record?.typeName !== 'shape') continue;
          if (!isCanvasEvidenceShape(record)) continue;
          shapeIds.push(record.id);
        }
        if (shapeIds.length) enqueueDigestDelete([], shapeIds);
      },
      { scope: 'document' }
    );

    return () => unlisten();
  }, [editor, canEdit, enqueueDigestDelete]);

  return null;
}
